import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trash2, RotateCcw, AlertTriangle, ArrowLeft, Calendar, Sprout, Beaker } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const fetchAuth = (path, opts = {}) =>
  fetch(`${API}${path}`, { credentials: "include", ...opts });

function daysUntilPurge(deletedAt) {
  if (!deletedAt) return null;
  const deleted = new Date(deletedAt);
  const purgeDate = new Date(deleted.getTime() + 30 * 24 * 60 * 60 * 1000);
  const diff = Math.ceil((purgeDate - new Date()) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

function formatDate(s, dateLocale) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString(dateLocale);
  } catch {
    return s;
  }
}

export default function Cestino() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "fr" ? "fr-FR" : "it-IT";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ treatments: [], fertilizations: [], vineyards: [] });
  const [tab, setTab] = useState("treatments");

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetchAuth("/api/trash");
      if (!r.ok) throw new Error("Errore caricamento");
      const d = await r.json();
      setData(d);
    } catch (e) {
      toast.error(t("cestino.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const restore = async (type, id) => {
    const endpoints = {
      treatments: `/api/treatments/${id}/restore`,
      fertilizations: `/api/fertilizations/${id}/restore`,
      vineyards: `/api/vineyards/${id}/restore`,
    };
    try {
      const r = await fetchAuth(endpoints[type], { method: "POST" });
      if (!r.ok) throw new Error();
      toast.success(t("cestino.restoreSuccess"));
      load();
    } catch {
      toast.error(t("cestino.restoreError"));
    }
  };

  const total = data.treatments.length + data.fertilizations.length + data.vineyards.length;

  const tabs = [
    { key: "treatments", label: t("cestino.tabTreatments"), icon: Beaker, count: data.treatments.length },
    { key: "fertilizations", label: t("cestino.tabFertilizations"), icon: Sprout, count: data.fertilizations.length },
    { key: "vineyards", label: t("cestino.tabVineyards"), icon: Calendar, count: data.vineyards.length },
  ];

  return (
    <div className="min-h-screen bg-stone-50 pb-24">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            data-testid="cestino-back-btn"
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-stone-100 rounded-lg"
            aria-label={t("cestino.backAriaLabel")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Trash2 className="w-6 h-6 text-rose-600" />
          <div>
            <h1 className="text-xl font-bold text-stone-900">{t("cestino.headerTitle")}</h1>
            <p className="text-xs text-stone-500">
              {t("cestino.headerSubtitlePrefix")} <strong>{t("cestino.headerSubtitleBold")}</strong>{t("cestino.headerSubtitleSuffix")}
            </p>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {!loading && total === 0 && (
          <div className="bg-white rounded-2xl p-12 text-center border border-stone-200">
            <Trash2 className="w-14 h-14 mx-auto text-stone-300 mb-4" />
            <h2 className="text-lg font-semibold text-stone-700 mb-2">{t("cestino.emptyTitle")}</h2>
            <p className="text-sm text-stone-500">
              {t("cestino.emptyText")}
            </p>
          </div>
        )}

        {total > 0 && (
          <>
            <div className="flex gap-2 mb-4 overflow-x-auto">
              {tabs.map((tabDef) => {
                const Icon = tabDef.icon;
                const active = tab === tabDef.key;
                return (
                  <button
                    key={tabDef.key}
                    data-testid={`cestino-tab-${tabDef.key}`}
                    onClick={() => setTab(tabDef.key)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition ${
                      active ? "bg-emerald-700 text-white" : "bg-white text-stone-700 border border-stone-200"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tabDef.label}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? "bg-emerald-900" : "bg-stone-100"}`}>
                      {tabDef.count}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="space-y-3">
              {data[tab].length === 0 && (
                <div className="bg-white rounded-xl p-8 text-center text-stone-500 text-sm border border-stone-200">
                  {t("cestino.emptyTypeText")}
                </div>
              )}

              {data[tab].map((item) => {
                const daysLeft = daysUntilPurge(item.deleted_at);
                const urgent = daysLeft !== null && daysLeft <= 7;
                let title, subtitle;
                if (tab === "treatments") {
                  title = item.product_name || t("cestino.defaultTreatmentTitle");
                  subtitle = `${formatDate(item.treatment_date, dateLocale)} · ${(item.target_diseases || []).join(", ") || "—"}`;
                } else if (tab === "fertilizations") {
                  title = item.product_name || t("cestino.defaultFertilizationTitle");
                  subtitle = `${formatDate(item.fertilization_date, dateLocale)} · ${item.quantity_used || "—"}`;
                } else {
                  title = item.name || t("cestino.defaultVineyardTitle");
                  subtitle = item.location_name || "";
                }

                return (
                  <div
                    key={item.id}
                    data-testid={`trash-item-${tab}-${item.id}`}
                    className="bg-white rounded-xl border border-stone-200 p-4 flex items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-stone-900 truncate">{title}</div>
                      <div className="text-sm text-stone-500 truncate">{subtitle}</div>
                      <div className={`mt-1 inline-flex items-center gap-1 text-xs font-semibold ${urgent ? "text-rose-600" : "text-stone-500"}`}>
                        {urgent && <AlertTriangle className="w-3.5 h-3.5" />}
                        {daysLeft === 0
                          ? t("cestino.purgeToday")
                          : t("cestino.purgeDays", { count: daysLeft })}
                      </div>
                    </div>
                    <button
                      data-testid={`restore-btn-${item.id}`}
                      onClick={() => restore(tab, item.id)}
                      className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap"
                    >
                      <RotateCcw className="w-4 h-4" />
                      {t("cestino.restoreButton")}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
